import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CatalogCard } from "../components";
import { EvaluationContext } from "../components/context_provider";
import { AnketaResourcesEnum, useApi } from "../../";
import { useParams } from "react-router";
import { CatalogObject } from "anketa-core/catalog_object";
import { Catalog } from "anketa-core/types";
import { useAnketa } from "../components/context_provider";
import { AnketaQuestionnaire } from "../types";
import { OpsanioCatalog, TestCatalog } from "../catalogs";
import { useAnketaAuth } from "../../auth/context";
import { EuiButton, EuiEmptyPrompt } from "@elastic/eui";
import { useAnketaI18n } from "../../internationalization";
import { FormSkeleton } from "src/components/skeleton";
import { useQuestionnaireSave } from "../save";

export const AnketaRender = ({
  updateCatalog,
  catalogIdOverwrite = null,
  questionnaireIdOverwrite = null,
}: {
  updateCatalog: (c: Catalog) => void;
  catalogIdOverwrite?: string | null;
  questionnaireIdOverwrite?: string | null;
}): JSX.Element => {
  const { catalogId, questionnaireId } = useParams();
  const { Get, Put } = useApi();
  const { keycloak, checkPermission, user } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const ctx = useAnketa();
  const evalCtx = useContext(EvaluationContext);
  const { markUnsaved } = useQuestionnaireSave();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  const effectiveCatalogId =
    catalogIdOverwrite !== null ? catalogIdOverwrite : catalogId;
  const effectiveQuestionnaireId =
    questionnaireIdOverwrite !== null
      ? questionnaireIdOverwrite
      : questionnaireId;

  const setQuestionnaire = async () => {
    if (canFetch) {
      setIsLoading(true);
      setLoadError(null);
      updateCatalog(TestCatalog.instance);
      // Drop any cached catalog from a previously opened questionnaire before
      // loading this one, so its facts (and id-keyed browser cache) cannot be
      // parsed into this questionnaire - including when the load below fails.
      OpsanioCatalog.clearInstance();
      const urlParams = `${effectiveCatalogId}/${effectiveQuestionnaireId}`;
      try {
        const response = await Get<AnketaQuestionnaire>({
          path: `v2/${AnketaResourcesEnum.questionnaires}/${urlParams}`,
          responseType: "json",
          token: keycloak?.token,
        });
        const { data } = response;
        let newCatalog: CatalogObject | null = null;

        if (data.questionnaireCatalog === "opsanio") {
          newCatalog = OpsanioCatalog.createInstance(
            data.id,
            data.questionnaireCatalog,
            data.members,
            checkPermission,
            data.content,
            keycloak.token,
            user,
          );
        }
        if (newCatalog === null) {
          throw new Error(
            `Unsupported questionnaire catalog "${data.questionnaireCatalog}"`,
          );
        }
        // Load the server answers BEFORE handing the catalog to the editors, so
        // no field ever mounts against a blank catalog.
        newCatalog.factsFromObject(data.content);
        if (data.content === null) {
          // First open: persist the catalog defaults so the server has content.
          Put({
            path: `v2/${AnketaResourcesEnum.questionnaires}/${effectiveCatalogId}/${effectiveQuestionnaireId}`,
            responseType: "json",
            token: keycloak?.token,
            value: {
              newContent: JSON.stringify(newCatalog.factsToObject()),
            },
          });
        }
        updateCatalog(newCatalog);
        setIsLoading(false);
      } catch (err) {
        // A failed load previously left a blank card with no recovery. Surface a
        // friendly error with a retry action instead.
        setLoadError(
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : JSON.stringify(err),
        );
        setIsLoading(false);
      }
    }
  };
  // Only (re)load when the actual questionnaire target changes - NOT on every
  // canFetch/keycloak change (the token is refreshed periodically). Re-running
  // setQuestionnaire toggles isLoading (which unmounts CatalogCard) and resets
  // the catalog via updateCatalog(TestCatalog.instance); doing that in the
  // background would tear down any open editor/modal and discard in-progress
  // edits - the cause of the "form refreshes / modal closes" instability.
  const loadedTargetRef = useRef<string | null>(null);
  const targetKey = `${effectiveCatalogId}/${effectiveQuestionnaireId}`;
  useEffect(() => {
    if (!canFetch) {
      return;
    }
    if (loadedTargetRef.current === targetKey) {
      return;
    }
    loadedTargetRef.current = targetKey;
    setQuestionnaire();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, targetKey]);

  // ---- Local draft persistence ---------------------------------------------
  // Auto-save to the server is DISABLED: answers are no longer pushed to the
  // server automatically on change. Each committed change still bumps
  // EvaluationContext.evaluationId; we mirror it to localStorage so an in-
  // progress questionnaire survives navigation / reloads, but the answers only
  // reach the server when the user explicitly saves (header Save button).
  const lastSavedEvalRef = useRef<number | null>(null);

  // Reset the autosave baseline whenever the load target changes so the next
  // questionnaire's first edit (not its load) is what triggers a save. Declared
  // BEFORE the autosave effect so on mount it nulls the baseline first, letting
  // the autosave effect then establish it - otherwise the first edit is missed.
  useEffect(() => {
    lastSavedEvalRef.current = null;
  }, [targetKey]);

  useEffect(() => {
    if (isLoading || loadError !== null) {
      return;
    }
    if (!effectiveCatalogId || !effectiveQuestionnaireId) {
      return;
    }
    // Establish a baseline on first run / after a (re)load - the load itself is
    // not a user edit and must not trigger a save.
    if (lastSavedEvalRef.current === null) {
      lastSavedEvalRef.current = evalCtx.evaluationId;
      return;
    }
    if (lastSavedEvalRef.current === evalCtx.evaluationId) {
      return;
    }
    lastSavedEvalRef.current = evalCtx.evaluationId;

    // Local durability only: survives navigation / reloads. No automatic
    // server write happens here - the user must save explicitly, so the header
    // indicator says there is something to save.
    ctx.saveToBrowser();
    markUnsaved();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evalCtx.evaluationId, isLoading, loadError]);

  if (loadError !== null) {
    return (
      <EuiEmptyPrompt
        iconType="alert"
        color="danger"
        title={
          <h2>
            {translate("anketa.questionnaire.load.error.title", {
              defaultMessage: "Could not load the questionnaire",
            })}
          </h2>
        }
        body={<p>{loadError}</p>}
        actions={
          <EuiButton color="primary" fill onClick={() => setQuestionnaire()}>
            {translate("anketa.questionnaire.load.error.retry", {
              defaultMessage: "Retry",
            })}
          </EuiButton>
        }
      />
    );
  }

  if (isLoading) {
    return <FormSkeleton />;
  } else {
    return <CatalogCard />;
  }
};
